import { useCallback, useState } from 'react';

import type {
  DashboardDataTablePaginationState,
  DashboardDataTableSortState,
  DashboardDataTableStateOptions,
} from './DashboardDataTable.types';

export function useDashboardDataTableState({
  initialPage = 0,
  initialPageSize,
  initialSort,
  totalItems,
  pageSizes,
  disabled,
}: DashboardDataTableStateOptions) {
  const [page, setPageState] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [sort, setSortState] = useState<DashboardDataTableSortState | undefined>(initialSort);
  const clampedPage = clampDashboardDataTablePage(page, totalItems, pageSize);
  const paginationValue: DashboardDataTablePaginationState = {
    page: clampedPage,
    pageSize,
    totalItems,
  };

  if (page !== clampedPage) {
    setPageState(clampedPage);
  }

  const setPage = useCallback(
    (nextPage: number) => {
      setPageState(clampDashboardDataTablePage(nextPage, totalItems, pageSize));
    },
    [pageSize, totalItems],
  );

  const setPageSize = useCallback((nextPageSize: number) => {
    setPageSizeState(nextPageSize);
    setPageState(0);
  }, []);

  const resetPage = useCallback(() => {
    setPageState(0);
  }, []);

  const setSort = useCallback((nextSort: DashboardDataTableSortState | undefined) => {
    setSortState(nextSort);
    setPageState(0);
  }, []);

  const handlePaginationChange = useCallback(
    (nextValue: DashboardDataTablePaginationState) => {
      setPageSizeState(nextValue.pageSize);
      setPageState(
        nextValue.pageSize !== pageSize
          ? 0
          : clampDashboardDataTablePage(nextValue.page, nextValue.totalItems, nextValue.pageSize),
      );
    },
    [pageSize],
  );

  return {
    page: clampedPage,
    pageSize,
    sort,
    setPage,
    setPageSize,
    setSort,
    resetPage,
    pagination: {
      value: paginationValue,
      pageSizes,
      disabled,
      onChange: handlePaginationChange,
    },
    sortProps: {
      value: sort,
      onChange: setSort,
    },
  };
}

function clampDashboardDataTablePage(page: number, totalItems: number, pageSize: number) {
  const normalizedPage = Math.max(0, Math.trunc(page));
  const normalizedPageSize = Math.max(1, Math.trunc(pageSize));
  const normalizedTotalItems = Math.max(0, Math.trunc(totalItems));
  if (normalizedTotalItems === 0) {
    return normalizedPage;
  }
  const lastPage = Math.max(0, Math.ceil(normalizedTotalItems / normalizedPageSize) - 1);

  return Math.min(normalizedPage, lastPage);
}
